import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {EQUIPMENT_SCHEMA_INTERFACE_ID} from 'instruments/equipment/EquipmentConstants';
import {
  SAMPLE_TYPE_FIELD_DISPLAY_NAME,
  SUBJECT_FIELD_DISPLAY_NAME,
  SUBJECT_LABEL_FIELD_DISPLAY_NAME,
  TREATMENT_GROUP_FIELD_DISPLAY_NAME,
  TRIAL_SAMPLE_SCHEMA_INTERFACE_ID,
  TRIAL_SUBJECT_SCHEMA_INTERFACE_ID,
  VISIT_NAME_FIELD_DISPLAY_NAME,
} from 'registry/trial-interfaces/TrialInterfaceConstants';
import {EXPIRATION_TRACKING_ENTITY_SCHEMA_INTERFACE_ID} from 'samples/valid-contents/ValidContentsConstants';
import {InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';

export interface StaticInterfaceEntityFieldsBannerStackProps {
  selectedInterfaceIds: Array<string>;
}

interface StaticInterfaceEntityFieldsProps {
  /**
   * First line of banner introducing the SSI requirements. Should include the SSI name.
   * Example: "To enable SSIName, please add the following entity fields:"
   */
  instructionsHeader: string;
  /**
   * Describe each entity field that must be added, including its definition and any required attributes.
   * If there are multiple required entity fields, each field should be described in a separate line.
   */
  fieldsInfo: React.ReactNode;
}

const styles = StyleSheet.create({
  leftAlignList: {
    paddingLeft: 0,
    // List items in InlineBanner have a margin of 24px, which is farther than needed here.
    marginLeft: -20,
  },
});

/**
 * Map of SSI ID to information about its entity fields, to render a banner over the entity fields table.
 * If an SSI does not require any entity fields, it should not be included in this map.
 */
const INTERFACE_ID_TO_WARNING_BANNER_PROPS: Record<string, StaticInterfaceEntityFieldsProps> = {
  [EXPIRATION_TRACKING_ENTITY_SCHEMA_INTERFACE_ID]: {
    instructionsHeader: 'To enable expiration tracking, add the following entity field:',
    fieldsInfo: 'Field name: "Expiration date" | Definition: "Datetime"',
  },
  [EQUIPMENT_SCHEMA_INTERFACE_ID]: {
    instructionsHeader: 'To configure as equipment, add the following entity fields:',
    fieldsInfo: (
      <div className={css(styles.leftAlignList)}>
        <ul>
          <li>Field name: "Serial number" | Required: &#10003; | Definition: "Text"</li>
          <li>Field name: "Model" | Required: &#10003; | Definition: "Entity" &rarr; select an entity</li>
          <li>Field name: "Calibration due date" | Definition: "Datetime"</li>
        </ul>
      </div>
    ),
  },
  [TRIAL_SUBJECT_SCHEMA_INTERFACE_ID]: {
    instructionsHeader: 'To configure as a trial subject, add the following entity field:',
    fieldsInfo: (
      <>Field name: "{SUBJECT_LABEL_FIELD_DISPLAY_NAME}" | Required: &#10003; | Definition: "Text"</>
    ),
  },
  [TRIAL_SAMPLE_SCHEMA_INTERFACE_ID]: {
    instructionsHeader: 'To configure as a trial sample, add the following entity fields:',
    fieldsInfo: (
      <div className={css(styles.leftAlignList)}>
        <ul>
          <li>Field name: "{SAMPLE_TYPE_FIELD_DISPLAY_NAME}" | Definition: "Text"</li>
          <li>Field name: "{VISIT_NAME_FIELD_DISPLAY_NAME}" | Definition: "Text"</li>
          <li>
            Field name: "{SUBJECT_FIELD_DISPLAY_NAME}" | Required: ✓ | Definition: "Entity → select the Trial
            Subject category"
          </li>
          <li>Field name: "{TREATMENT_GROUP_FIELD_DISPLAY_NAME}" | Definition: "Text"</li>
        </ul>
      </div>
    ),
  },
};

function styleBannerProps(inputProps: StaticInterfaceEntityFieldsProps): InlineBannerProps {
  return {
    bannerType: InlineBannerType.WARNING,
    text: inputProps.instructionsHeader,
    supportingInfo: inputProps.fieldsInfo,
  };
}

/**
 * Renders a stack of banners for each selected static schema interface (SSI) that has specific entity field
 * requirements. Intended to be used when a user is creating a new entity schema: schema definition is in
 * progress until the user hits save, so this banner stack helps users correctly define a new entity schema
 * with SSIs enabled.
 *
 * If this component is generalized to support non-static interfaces ("schema fieldsets"), it should be
 * renamed and moved to client/schemas/schema-interface.
 */
export default function StaticInterfaceEntityFieldsBannerStack(
  props: StaticInterfaceEntityFieldsBannerStackProps
): JSX.Element | null {
  if (props.selectedInterfaceIds.length > 0) {
    const bannerProps: Array<InlineBannerProps> = props.selectedInterfaceIds
      .map((interfaceId) => INTERFACE_ID_TO_WARNING_BANNER_PROPS[interfaceId])
      .filter((interfaceProps) => interfaceProps != null)
      .map((interfaceProps) => styleBannerProps(interfaceProps));
    if (bannerProps.length > 0) {
      return (
        <div className="u-moderate-spacing-bottom">
          <InlineBannerStack messages={bannerProps} />
        </div>
      );
    }
  }
  return null;
}
